import type { ClaimEvidence, DeckIntelligence } from '../types';
import { parseRiskLevel, SECTION_SCORE_KEYS } from './deckIntelligenceScores';

export type OpportunityWeakPointCategory =
  | 'section'
  | 'claim'
  | 'risk'
  | 'gap'
  | 'assumption'
  | 'deathReason';

export type OpportunityWeakPoint = {
  /** Stable id derived from category + position, used for React keys and job tracking. */
  id: string;
  category: OpportunityWeakPointCategory;
  /** 0–10, higher = more severe. */
  severity: number;
  title: string;
  detail: string;
};

const WEAK_SECTION_THRESHOLD = 7;
const HIGH_RISK_THRESHOLD = 8;

const CLAIM_SEVERITY: Record<ClaimEvidence, number> = {
  likely_false: 9,
  speculative: 7,
  weak: 5,
  supported: 0,
};

const CLAIM_EVIDENCE_LABEL: Record<ClaimEvidence, string> = {
  likely_false: 'likely false',
  speculative: 'speculative',
  weak: 'weakly supported',
  supported: 'supported',
};

export function weakPointCategoryLabel(category: OpportunityWeakPointCategory): string {
  switch (category) {
    case 'section':
      return 'Weak dimension';
    case 'claim':
      return 'Shaky claim';
    case 'risk':
      return 'High risk';
    case 'gap':
      return 'Missing information';
    case 'assumption':
      return 'Dangerous assumption';
    case 'deathReason':
      return 'Death reason';
    default:
      return category;
  }
}

/**
 * Derive a ranked list of weak points from an existing analysis — no LLM call.
 * Highest severity first.
 */
export function deriveOpportunityWeakPoints(
  intelligence: DeckIntelligence
): OpportunityWeakPoint[] {
  const points: OpportunityWeakPoint[] = [];

  if (intelligence.sectionScores) {
    for (const { key, label } of SECTION_SCORE_KEYS) {
      const score = intelligence.sectionScores[key];
      if (typeof score !== 'number' || score >= WEAK_SECTION_THRESHOLD) continue;
      points.push({
        id: `section:${key}`,
        category: 'section',
        severity: Math.min(10, Math.max(0, 10 - score)),
        title: `${label} scores ${score}/10`,
        detail: intelligence[key] ?? '',
      });
    }
  }

  intelligence.claims.forEach((claim, index) => {
    const severity = CLAIM_SEVERITY[claim.evidenceEvaluation];
    if (severity <= 0) return;
    points.push({
      id: `claim:${index}`,
      category: 'claim',
      severity,
      title: claim.name,
      detail: `Evidence rated ${CLAIM_EVIDENCE_LABEL[claim.evidenceEvaluation]}.`,
    });
  });

  intelligence.risks.forEach((risk, index) => {
    const level = parseRiskLevel(risk.riskEvaluation);
    if (level < HIGH_RISK_THRESHOLD) return;
    points.push({
      id: `risk:${index}`,
      category: 'risk',
      severity: level,
      title: risk.description,
      detail: risk.riskEvaluation,
    });
  });

  intelligence.missing.forEach((item, index) => {
    const trimmed = item.trim();
    if (!trimmed) return;
    points.push({
      id: `gap:${index}`,
      category: 'gap',
      severity: 4,
      title: trimmed,
      detail: 'The analysis flagged this as missing from the idea.',
    });
  });

  const assumption = intelligence.analysis.mostDangerousAssumption.trim();
  if (assumption) {
    points.push({
      id: 'assumption:0',
      category: 'assumption',
      severity: 7,
      title: assumption,
      detail: 'The analysis considers this the most dangerous assumption behind the idea.',
    });
  }

  intelligence.analysis.top3DeathReasons.forEach((reason, index) => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    points.push({
      id: `deathReason:${index}`,
      category: 'deathReason',
      severity: 6,
      title: trimmed,
      detail: 'Listed among the top reasons this idea could die.',
    });
  });

  return points.sort((a, b) => b.severity - a.severity);
}
